import React, { useEffect, useMemo, useState } from "react";
import { fmtDuration } from "./SwachhataBits";

const KEYS = ["A", "B", "C", "D"];

// One question at a time. `answers` is { [qid]: 'A'|'B'|'C'|'D' } and is owned
// by the parent (persisted to localStorage there so a reload doesn't lose it).
// `onExit` (optional) adds an "Exit quiz" button; exiting asks first and keeps
// the attempt — answers are already saved by the parent, so it can be resumed.
export default function SwachhataQuiz({ questions, answers, onAnswer, onSubmit, submitting, startedAt, error, onExit }) {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState("fwd");
  const [confirm, setConfirm] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const q = questions[i];
  const total = questions.length;
  const answered = useMemo(() => questions.filter((x) => answers[x.id]).length, [questions, answers]);
  const unanswered = total - answered;

  useEffect(() => {
    const t0 = startedAt ? new Date(startedAt).getTime() : Date.now();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - t0) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const go = (n) => {
    if (n < 0 || n >= total) return;
    setDir(n > i ? "fwd" : "back");
    setI(n);
    setConfirm(false);
    setExiting(false);
  };

  // keyboard: A-D select, arrows navigate
  useEffect(() => {
    const onKey = (e) => {
      if (confirm || exiting) return;
      const k = e.key.toUpperCase();
      if (KEYS.includes(k) && q) onAnswer(q.id, k);
      else if (e.key === "ArrowRight") go(i + 1);
      else if (e.key === "ArrowLeft") go(i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, q, confirm, exiting, onAnswer]);

  if (!q) return null;
  const pct = Math.round((answered / total) * 100);
  const isLast = i === total - 1;

  return (
    <div className="shs-quiz">
      <div className="shs-quiz__head">
        <div className="shs-quiz__meta">
          <b>{`Question ${i + 1}`}<span style={{ color: "var(--shs-muted)", fontWeight: 600, marginLeft: "0.3em" }}>{`of ${total}`}</span></b>
          <span className="shs-quiz__cat">{q.category}</span>
          <span className="shs-quiz__tools">
            <span className="shs-timer"><i />{fmtDuration(elapsed)}</span>
            {onExit && (
              <button type="button" className="shs-btn shs-btn--exit shs-btn--sm" onClick={() => { setConfirm(false); setExiting(true); }} disabled={submitting}>
                ✕ Exit quiz
              </button>
            )}
          </span>
        </div>
        <div className="shs-bar" aria-label={`${answered} of ${total} answered`}><i style={{ width: `${pct}%` }} /></div>
      </div>

      <div className="shs-quiz__body">
        {exiting ? (
          <div className="shs-confirm">
            <h3>Exit the quiz?</h3>
            <p>{`Your answers so far (${answered} of ${total}) are saved on this device. Open the quiz page again to continue where you left off.`}</p>
            <div className="shs-confirm__actions">
              <button type="button" className="shs-btn shs-btn--exit" onClick={onExit}>Yes, exit</button>
              <button type="button" className="shs-btn shs-btn--primary" onClick={() => setExiting(false)}>Continue the quiz</button>
            </div>
          </div>
        ) : confirm ? (
          <div className="shs-confirm">
            <h3>{unanswered ? `${unanswered} question${unanswered > 1 ? "s" : ""} still unanswered` : "Submit your answers?"}</h3>
            <p>
              {unanswered
                ? "Unanswered questions are marked wrong. You can go back and answer them, or submit as is."
                : "You have answered all 10 questions. Once submitted, your answers cannot be changed."}
            </p>
            {error && <div className="shs-error">{error}</div>}
            <div className="shs-confirm__actions">
              <button type="button" className="shs-btn shs-btn--primary" onClick={onSubmit} disabled={submitting}>
                {submitting ? "Submitting…" : "Yes, submit"}
              </button>
              <button type="button" className="shs-btn shs-btn--ghost" onClick={() => setConfirm(false)} disabled={submitting}>
                {unanswered ? "Go back and answer" : "Review once more"}
              </button>
            </div>
          </div>
        ) : (
          <div className={`shs-qcard ${dir === "back" ? "shs-qcard--back" : ""}`} key={q.id}>
            <div className="shs-qcard__num">Q{i + 1}</div>
            <div className="shs-qcard__text">{q.q}</div>
            <div className="shs-options" role="radiogroup" aria-label={`Question ${i + 1}`}>
              {KEYS.map((k, oi) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={answers[q.id] === k}
                  className={`shs-option ${answers[q.id] === k ? "is-selected" : ""}`}
                  style={{ "--i": oi }}
                  onClick={() => onAnswer(q.id, k)}
                >
                  <span className="shs-option__key">{k}</span>
                  <span className="shs-option__text">{q.options[k]}</span>
                  <span className="shs-option__tick">✔</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="shs-quiz__nav">
        <div className="shs-quiz__dots" aria-hidden="true">
          {questions.map((x, n) => (
            <button
              key={x.id}
              type="button"
              className={`shs-qdot ${answers[x.id] ? "is-answered" : ""} ${n === i ? "is-current" : ""}`}
              onClick={() => go(n)}
              title={`Question ${n + 1}`}
            >
              {n + 1}
            </button>
          ))}
        </div>
        <div className="shs-footer__actions">
          <button type="button" className="shs-btn shs-btn--ghost shs-btn--sm" onClick={() => go(i - 1)} disabled={i === 0 || confirm || exiting}>← Previous</button>
          {isLast || confirm ? (
            <button type="button" className="shs-btn shs-btn--orange shs-btn--sm" onClick={() => { setExiting(false); setConfirm(true); }} disabled={confirm || submitting}>
              Submit quiz
            </button>
          ) : (
            <button type="button" className="shs-btn shs-btn--primary shs-btn--sm" onClick={() => go(i + 1)}>
              Next <span className="shs-btn__arrow">→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
